import { message } from 'antd';
import type { AxiosResponse } from 'axios';
import flow from 'lodash/flow';
import filter from 'lodash/fp/filter';
import flatten from 'lodash/fp/flatten';
import map from 'lodash/fp/map';
import uniq from 'lodash/fp/uniq';
import pick from 'lodash/pick';
import Router from 'next/router';
import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { getType } from 'typesafe-actions';
import type { ActionType } from 'typesafe-actions';

import { LedgerItemType } from '@evenfinancial/finance-client';
import type {
  AccountDeactivation,
  BillingCycleCreateData,
  TermUnit,
} from '@evenfinancial/finance-client';

import { client } from '@portal/api-client/src/client';
import type {
  AccountSelfService,
  PortalAccountWriteData,
  TempAccount,
} from '@portal/api-models';
import { getPortalError } from '@portal/common';

import { getUserByUuidRequest } from '../user/sagas';
import { selectPermissions, selectUsersByUuid } from '../user/selectors';
import type { UserState } from '../user/types';

import * as actions from './actions';
import { selectFirstAccount } from './selectors';

export function* getAccounts(
  action?: ActionType<typeof actions.accountRequestAction.request>
) {
  try {
    const { shouldGetSubAccountOwners } = yield select(selectPermissions);

    const { data: accounts }: AxiosResponse<TempAccount[]> = yield call(
      [client, 'get'],
      '/finance/accounts',
      {
        params: { limit: 1e5 },
      }
    );

    if (shouldGetSubAccountOwners) {
      yield call(getAccountOwners, accounts, action?.payload.cookie);
    }

    yield put(actions.accountRequestAction.success(accounts));
  } catch (err: any) {
    yield put(actions.accountRequestAction.failure(err));
  }
}

export function* getByUuid(
  action: ActionType<typeof actions.accountByUuidAction.request>
) {
  const { uuid, cookie } = action.payload;

  try {
    const { shouldGetSubAccountOwners } = yield select(selectPermissions);

    const { data: account }: AxiosResponse<TempAccount> = yield call(
      [client, 'get'],
      `/finance/accounts/${uuid}`
    );

    if (shouldGetSubAccountOwners) {
      yield call(getAccountOwners, [account], cookie);
    }

    yield put(actions.accountByUuidAction.success(account));
  } catch (err: any) {
    yield call([Router, 'replace'], '/404');
    yield put(actions.accountByUuidAction.failure(err));
  }
}

function* getAccountOwners(
  accounts: TempAccount[],
  cookie: string | undefined
) {
  yield getOwners<TempAccount>(accounts, 'accountOwners', cookie);
}

export function* getOwners<T>(
  records: T[],
  ownersKey: keyof T,
  cookie: string | undefined
) {
  const ownerKeys = [
    'supplyPartnerManager',
    'supplyYieldManager',
    'demandPartnerManager',
    'demandYieldManager',
  ];

  try {
    const usersByUuid: UserState['rowsByUuid'] = yield select(
      selectUsersByUuid
    );

    const ownerUuids: string[] = flow([
      // Extracting owners uuids from records.
      map((record: T) => {
        return record[ownersKey]
          ? Object.values(pick(record[ownersKey], ownerKeys))
          : [];
      }),
      flatten,
      uniq,
      // Filtering out users that are already in memory and do not need to be
      // queried again.
      filter((uuid: string) =>
        Object.prototype.hasOwnProperty.call(usersByUuid, uuid)
      ),
    ])(records);

    for (const uuid of ownerUuids) {
      yield call(getUserByUuidRequest, { cookie, uuid });
    }
  } catch (error) {
    console.error(error);
  }
}

export function* createAccount(
  action: ActionType<typeof actions.accountCreateRequestAction.request>
) {
  try {
    const formParams = action.payload;
    const { accountOwners } = action.payload;

    const params: PortalAccountWriteData = {
      accountOwners,
      billingName: formParams.billingName!,
      displayName: formParams.displayName,
      name: formParams.name!,
    };

    const billingCycle: Omit<BillingCycleCreateData, 'accountId'>[] = [];

    if (formParams?.payable?.[0]?.termUnit) {
      billingCycle.push({
        effectiveAt:
          formParams?.payable[0]?.effectiveAt?.toISOString().split('T')[0] ??
          '',
        ledgerItemType: LedgerItemType.Payable,
        netD: parseInt(formParams.payable[0].netD!, 10),
        termUnit: formParams.payable[0].termUnit as TermUnit,
      });
    }

    if (formParams?.receivable?.[0]?.termUnit) {
      billingCycle.push({
        effectiveAt:
          formParams?.receivable[0]?.effectiveAt
            ?.toISOString()
            ?.split('T')[0] ?? '',
        ledgerItemType: LedgerItemType.Receivable,
        netD: parseInt(formParams.receivable[0].netD!, 10),
        termUnit: formParams.receivable[0].termUnit as TermUnit,
      });
    }

    params.billingCycles = billingCycle;

    const { data: created }: AxiosResponse<TempAccount> = yield call(
      [client, 'post'],
      '/finance/accounts',
      params
    );

    yield call([message, 'success'], 'The account was created successfully');

    yield put(actions.accountCreateRequestAction.success(created));

    yield call([Router, 'push'], `/accounts/${created.uuid}`);
  } catch (err: any) {
    const portalError = getPortalError(err);

    yield put(actions.accountCreateRequestAction.failure(portalError));
  }
}

export function* updateAccount(
  action: ActionType<typeof actions.accountUpdateRequestAction.request>
) {
  const formParams = action.payload;

  try {
    const { data: updated }: AxiosResponse<TempAccount> = yield call(
      [client, 'patch'],
      `/finance/accounts/${formParams.uuid}`,
      formParams
    );

    yield call([message, 'success'], 'The account was updated successfully');
    yield put(actions.accountUpdateRequestAction.success(updated));

    yield put(actions.accountByUuidAction.request(updated));
  } catch (err: any) {
    yield put(actions.accountUpdateRequestAction.failure(err));
  }
}

export function* updateAccountPayoutSettings(
  action: ActionType<
    typeof actions.accountUpdatePayoutSettingsRequestAction.request
  >
) {
  try {
    const account: TempAccount = yield select(selectFirstAccount);
    const formParams = action.payload;

    const updatedAccount = {
      ...account,
      billingEmail: formParams.billingEmail,
      w9File: {
        data: formParams.w9File?.file.url,
        name: formParams.w9File?.file.name,
      },
    };
    const { data: updated }: AxiosResponse<TempAccount> = yield call(
      [client, 'patch'],
      `/finance/accounts/${updatedAccount.uuid}`,
      updatedAccount
    );

    yield call(
      [message, 'success'],
      'You have successfully submitted your information!'
    );
    yield put(
      actions.accountUpdatePayoutSettingsRequestAction.success(updated)
    );
    yield put(actions.accountByUuidAction.request(updated));
  } catch (err: any) {
    yield put(actions.accountUpdateRequestAction.failure(err));
  }
}

export function* createAccountDeactivations(
  action: ActionType<typeof actions.createAccountDeactivationsAction.request>
) {
  const { accountIds } = action.payload;

  try {
    const { data: createDeactivations }: AxiosResponse<AccountDeactivation[]> =
      yield call([client, 'post'], '/finance/deactivations/accounts', {
        accountIds,
      });

    yield call(
      [message, 'success'],
      'The Account Status Was Successfully Updated'
    );
    yield put(
      actions.createAccountDeactivationsAction.success(createDeactivations)
    );
  } catch (err: any) {
    yield put(actions.createAccountDeactivationsAction.failure(err));
  }
}

export function* getDeactivationAccounts() {
  try {
    const { data: accountsDeactivation }: AxiosResponse<AccountDeactivation[]> =
      yield call([client, 'get'], '/finance/deactivations/accounts');

    yield put(
      actions.accountDeactivationRequestAction.success(accountsDeactivation)
    );
  } catch (err: any) {
    yield put(actions.accountDeactivationRequestAction.failure(err));
  }
}

export function* deleteAccountDeactivation(
  action: ActionType<typeof actions.deleteAccountDeactivationAction.request>
) {
  const { deactivationIds } = action.payload;

  try {
    const { data: deleteDeactivations }: AxiosResponse<AccountDeactivation[]> =
      yield call([client, 'put'], '/finance/deactivations/accounts', {
        ids: deactivationIds,
      });

    yield call(
      [message, 'success'],
      'The Account Status Was Successfully Updated'
    );
    yield put(
      actions.deleteAccountDeactivationAction.success(deleteDeactivations)
    );
  } catch (err: any) {
    yield put(actions.deleteAccountDeactivationAction.failure(err));
  }
}

export function* getSelfServiceAccounts() {
  try {
    const { data: accounts }: AxiosResponse<AccountSelfService[]> = yield call(
      [client, 'get'],
      '/analytics/self-service'
    );

    yield put(actions.accountsSelfServiceRequestAction.success(accounts));
  } catch (err: any) {
    yield put(actions.accountsSelfServiceRequestAction.failure(err));
  }
}

export function* accountSaga() {
  yield takeEvery(
    getType(actions.accountDeactivationRequestAction.request),
    getDeactivationAccounts
  );

  yield takeEvery(
    getType(actions.createAccountDeactivationsAction.request),
    createAccountDeactivations
  );

  yield takeEvery(
    getType(actions.deleteAccountDeactivationAction.request),
    deleteAccountDeactivation
  );

  yield takeEvery(
    getType(actions.accountCreateRequestAction.request),
    createAccount
  );

  yield takeEvery(getType(actions.accountRequestAction.request), getAccounts);

  yield takeEvery(getType(actions.accountByUuidAction.request), getByUuid);

  yield takeEvery(
    getType(actions.accountUpdateRequestAction.request),
    updateAccount
  );
  yield takeLatest(
    getType(actions.accountUpdatePayoutSettingsRequestAction.request),
    updateAccountPayoutSettings
  );

  //
  yield takeLatest(
    getType(actions.accountsSelfServiceRequestAction.request),
    getSelfServiceAccounts
  );
}
